import React, { useEffect, useState } from "react";
import MatchStats from "../common/MatchStats";
import NoDataFound from "../common/NoDataFound";
import { calculatePercentagestats } from "../../utils/generalLiveCenter";
import { loadStyles } from "../../utils/cssLoader";

const TennisStats = ({
  data,
  loadCSS = true,
  dataClient = false,
  title,
}) => {
  const [clientData, setClientData] = useState(null);
  const [selectedSet, setSelectedSet] = useState("all_sets");

  useEffect(() => {
    if (!dataClient) return;
    const container = document?.querySelector(".microservice-cont");
    const raw = container?.getAttribute("data-listingdata");
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw)?.data?.data;
      setClientData(parsed || null);
    } catch (err) {
      console.error("Failed to parse data-listingdata:", err);
    }
  }, [dataClient]);

  useEffect(() => {
    if (loadCSS === "true") {
      loadStyles("tennisstats");
    }
  }, [loadCSS]);

  const matchStats = dataClient ? clientData : data;

  if (!matchStats) {
    return <NoDataFound />;
  }

  // Dynamically get all available sets from matchStats (excluding 'match')
  const allSets = Object.keys(matchStats).filter(
    (key) =>
      key !== "match" && matchStats[key] && typeof matchStats[key] === "object"
  );

  const getStatsCategories = () => {
    const serviceStatsKeys = new Set();
    const pointsStatsKeys = new Set();

    allSets.forEach((setKey) => {
      const setData = matchStats[setKey];
      if (setData) {
        // Extract stats from service, return, and games categories
        ["service", "return", "games"].forEach((category) => {
          if (setData[category]) {
            Object.keys(setData[category]).forEach((statKey) => {
              serviceStatsKeys.add(statKey);
            });
          }
        });

        // Extract stats from points category
        if (setData.points) {
          Object.keys(setData.points).forEach((statKey) => {
            pointsStatsKeys.add(statKey);
          });
        }
      }
    });

    return {
      serviceStatsKeys: Array.from(serviceStatsKeys),
      pointsStatsKeys: Array.from(pointsStatsKeys),
    };
  };

  const { serviceStatsKeys, pointsStatsKeys } = getStatsCategories();

  // Get service stats based on selected set
  const getServiceStats = () => {
    if (selectedSet === "all_sets") {
      const combined = {};

      allSets.forEach((setKey) => {
        const setData = matchStats[setKey];
        if (setData) {
          Object.keys(setData).forEach((category) => {
            if (setData[category]) {
              Object.keys(setData[category]).forEach((statKey) => {
                if (serviceStatsKeys.includes(statKey)) {
                  if (!combined[statKey]) {
                    combined[statKey] = { ht: 0, at: 0 };
                  }
                  combined[statKey].ht += setData[category][statKey]?.ht || 0;
                  combined[statKey].at += setData[category][statKey]?.at || 0;
                }
              });
            }
          });
        }
      });

      return combined;
    }

    const setData = matchStats[selectedSet];
    if (!setData) return {};

    const serviceStats = {};
    Object.keys(setData).forEach((category) => {
      if (setData[category]) {
        Object.keys(setData[category]).forEach((statKey) => {
          if (serviceStatsKeys.includes(statKey)) {
            serviceStats[statKey] = setData[category][statKey];
          }
        });
      }
    });

    return serviceStats;
  };

  // Get points stats based on selected set
  const getPointsStats = () => {
    if (selectedSet === "all_sets") {
      const combined = {};

      allSets.forEach((setKey) => {
        const setData = matchStats[setKey];
        if (setData) {
          Object.keys(setData).forEach((category) => {
            if (setData[category]) {
              Object.keys(setData[category]).forEach((statKey) => {
                if (pointsStatsKeys.includes(statKey)) {
                  if (!combined[statKey]) {
                    combined[statKey] = { ht: 0, at: 0 };
                  }
                  combined[statKey].ht += setData[category][statKey]?.ht || 0;
                  combined[statKey].at += setData[category][statKey]?.at || 0;
                }
              });
            }
          });
        }
      });

      return combined;
    }

    const setData = matchStats[selectedSet];
    if (!setData) return {};

    const pointsStats = {};
    Object.keys(setData).forEach((category) => {
      if (setData[category]) {
        Object.keys(setData[category]).forEach((statKey) => {
          if (pointsStatsKeys.includes(statKey)) {
            pointsStats[statKey] = setData[category][statKey];
          }
        });
      }
    });

    return pointsStats;
  };

  const serviceStats = getServiceStats();
  const pointsStats = getPointsStats();

  // Filter out service stats with zero values and convert to array format
  const filteredServiceStats = Object.keys(serviceStats)
    .filter((key) => {
      const stat = serviceStats[key];
      return stat && (stat.ht !== 0 || stat.at !== 0);
    })
    .map((key) => ({
      type: key,
      ht: serviceStats[key].ht,
      at: serviceStats[key].at,
      htPercent: calculatePercentagestats(serviceStats[key].ht, serviceStats[key].at),
      atPercent: calculatePercentagestats(serviceStats[key].at, serviceStats[key].ht),
    }));

  // Filter out points stats with zero values and convert to array format
  const filteredPointsStats = Object.keys(pointsStats)
    .filter((key) => {
      const stat = pointsStats[key];
      return stat && (stat.ht !== 0 || stat.at !== 0);
    })
    .map((key) => ({
      type: key,
      ht: pointsStats[key].ht,
      at: pointsStats[key].at,
      htPercent: calculatePercentagestats(pointsStats[key].ht, pointsStats[key].at),
      atPercent: calculatePercentagestats(pointsStats[key].at, pointsStats[key].ht),
    }));

  // Show all sets that exist in matchStats
  const availableSets = Object.keys(matchStats).filter(
    (key) => matchStats[key] && typeof matchStats[key] === "object"
  );

  const sets = [
    { key: "all_sets", label: "All" },
    ...availableSets.map((set) => ({
      key: set,
      label:
        set === "match"
          ? "Match"
          : set.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase()),
    })),
  ];

  return (
    <div className="tennis_stats_container">
      {title && <h1>{title}</h1>}

      {availableSets.length > 0 && (
        <div className="stats_filters">
          <div className="set_selector">
            {sets.map((set) => (
              <button
                key={set.key}
                className={`filter_button ${selectedSet === set.key ? "active" : ""}`}
                onClick={() => setSelectedSet(set.key)}
              >
                {set.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {filteredServiceStats.length > 0 && (
        <>
          <div className="stats_title">
            <h2>Service</h2>
          </div>
          <div className="stats-holder">
            <MatchStats stats={filteredServiceStats} />
          </div>
        </>
      )}

      {filteredPointsStats.length > 0 && (
        <>
          <div className="stats_title">
            <h2>Points</h2>
          </div>
          <div className="stats-holder">
            <MatchStats stats={filteredPointsStats} />
          </div>
        </>
      )}

      {filteredServiceStats.length === 0 && filteredPointsStats.length === 0 && (
        <NoDataFound />
      )}
    </div>
  );
};

export default TennisStats;
